import type { FetchResult } from "@apollo/client";
import type { GraphQLError } from "graphql";
import { camelCase, isUndefined } from "lodash";
import type { NavigateFunction } from "react-router";

import { logOut } from "utils/logout";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

const getErrors = <Type,>(
  results: FetchResult<Type>[],
): (readonly GraphQLError[])[] =>
  results
    .map((result): readonly GraphQLError[] | undefined =>
      "errors" in result ? result.errors : undefined,
    )
    .filter(
      (
        optionalErrors: readonly GraphQLError[] | undefined,
      ): optionalErrors is readonly GraphQLError[] =>
        !isUndefined(optionalErrors),
    );

const LOGOUT_ERRORS = new Set([
  "Exception - Invalid Authorization",
  "Login required",
  "Exception - User token has expired",
]);

const ACCESS_DENIED_ERRORS = new Set([
  "Access denied",
  "Access denied or group not found",
  "Access denied or tag not found",
  "Access denied or organization not found",
  "Exception - Event not found",
]);

interface IGraphQLErrorExtensions {
  readonly branch?: string;
  readonly code?: string;
  readonly group?: string;
  readonly role?: string;
  readonly url?: string;
}

const getGraphQLErrorExtensions = (
  error: GraphQLError,
): IGraphQLErrorExtensions | undefined =>
  error.extensions as IGraphQLErrorExtensions | undefined;

const isVulnMarkedAsFpGraphQLError = (error: GraphQLError): boolean =>
  getGraphQLErrorExtensions(error)?.code === "VULN_MARKED_AS_FP";

const ROOT_CONFLICT_KEYS: Record<string, string | undefined> = {
  REPEATED_ROOT_SAME_BRANCH: "group.scope.common.errors.duplicateUrlSameBranch",
  REPEATED_ROOT_SAME_GROUP: "group.scope.common.errors.duplicateUrlSameGroup",
  TWO_ADVANCED_ROOT_REGISTRATIONS:
    "group.scope.common.errors.advancedPlanConflict",
};

const getRootConflictMessage = (error: GraphQLError): string | undefined => {
  const ext = getGraphQLErrorExtensions(error);
  if (ext === undefined) {
    return undefined;
  }
  const key = ROOT_CONFLICT_KEYS[String(ext.code ?? "")];
  if (key === undefined) {
    return undefined;
  }

  return translate.t(key, {
    branch: ext.branch,
    group: ext.group,
    url: ext.url,
  });
};

const translateRole = (role: string): string =>
  translate.t(`userModal.roles.${camelCase(role)}`, { defaultValue: role });

const getOrganizationRoleMismatch = (
  error: GraphQLError,
): string | undefined => {
  const ext = getGraphQLErrorExtensions(error);
  if (ext?.code !== "ORGANIZATION_ROLE_MISMATCH") {
    return undefined;
  }
  const role = ext.role ?? "";

  return translate.t("validations.organizationRoleMismatch", {
    role: translateRole(role),
  });
};

const handleSessionError = (error: GraphQLError): boolean => {
  if (!LOGOUT_ERRORS.has(error.message)) return false;
  logOut("assign");

  return true;
};

const handleGraphQLError = (
  error: GraphQLError,
  navigate: NavigateFunction,
): boolean => {
  if (handleSessionError(error)) return true;
  if (ACCESS_DENIED_ERRORS.has(error.message)) {
    msgError(translate.t("groupAlerts.accessDenied"));
    void navigate("/home");

    return true;
  }
  if (error.message === "Exception - Unsanitized input found") {
    msgError(translate.t("validations.unsanitizedInputFound"));

    return true;
  }
  if (error.message === "Internal attribute not accessible") {
    msgError(
      translate.t("group.frozen.description"),
      translate.t("group.frozen.title", { groupName: "" }),
    );
    void navigate("/home");

    return true;
  }

  return false;
};

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve): void => {
    setTimeout(resolve, ms);
  });

export {
  getErrors,
  getOrganizationRoleMismatch,
  getRootConflictMessage,
  handleGraphQLError,
  handleSessionError,
  isVulnMarkedAsFpGraphQLError,
  sleep,
  translateRole,
};
